import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Search, ArrowDownNarrowWide } from "lucide-react";
import { TicketCard, TICKET_CATEGORIES } from "../components/TicketCard";
import TicketModal from "../components/TicketModal";
import { useAuth } from "../lib/auth";

export default function Tickets() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [tab, setTab] = useState("active");
  const [sortBy, setSortBy] = useState("recent"); // recent | priority
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/tickets");
    setTickets(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = tickets.filter((t) => t.status === tab);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((t) => `${t.title} ${t.description} ${t.page_name}`.toLowerCase().includes(s));
    }
    if (sortBy === "priority") {
      list = [...list].sort((a, b) => (b.priority || 0) - (a.priority || 0) || (b.created_at || "").localeCompare(a.created_at || ""));
    } else {
      list = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return list;
  }, [tickets, tab, sortBy, search]);

  const counts = useMemo(() => ({
    active: tickets.filter((t) => t.status === "active").length,
    complete: tickets.filter((t) => t.status === "complete").length,
  }), [tickets]);

  return (
    <div data-testid="tickets-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Portal</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Work Tickets</h1>
          <p className="mt-3 text-sm leading-relaxed text-zinc-600">
            Leave a ticket and we'll get back to you within 24–48 hours.<br />
            For urgent matters, additional info that won't fit here, or if you just want to grab coffee — feel free to reach out directly:
          </p>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-zinc-700">
            <a href="mailto:creative@deepthreedesign.com" className="hover:text-[#0066ff]">📧 creative@deepthreedesign.com</a>
            <a href="tel:+16103318612" className="hover:text-[#0066ff]">📱 (610) 331-8612 — call or text anytime</a>
          </div>
        </div>
        <button
          data-testid="tickets-new-button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white transition-all hover:bg-[#0052cc] active:scale-[0.98]"
        >
          <Plus size={16} strokeWidth={1.8} /> New Ticket
        </button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-1">
          {[
            { id: "active", label: "Active" },
            { id: "complete", label: "Completed" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`tickets-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                tab === t.id ? "bg-[#E5F0FF] text-[#0066ff]" : "text-zinc-600 hover:bg-zinc-50"
              }`}
            >
              {t.label}
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tab === t.id ? "bg-white text-[#0066ff]" : "bg-zinc-100 text-zinc-500"}`}>
                {counts[t.id]}
              </span>
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            data-testid="tickets-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tickets"
            className="w-64 rounded-md border border-zinc-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
          />
        </div>
        <div className="flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 py-1 text-xs text-zinc-600">
          <ArrowDownNarrowWide size={12} />
          <select
            data-testid="tickets-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-transparent px-1 py-1 text-xs focus:outline-none"
          >
            <option value="recent">Most Recent</option>
            <option value="priority">Most Important</option>
          </select>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {filtered.map((t) => (
          <TicketCard key={t.id} ticket={t} onClick={() => navigate(`/tickets/${t.id}`)} />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-zinc-200 bg-white p-12 text-center">
            <p className="text-sm text-zinc-500">{tab === "active" ? "No active tickets yet." : "No completed tickets yet."}</p>
            {tab === "active" && (
              <button data-testid="tickets-empty-create" onClick={() => setCreating(true)} className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-3 py-2 text-sm font-medium text-white hover:bg-[#0052cc]">
                <Plus size={14} /> Create your first ticket
              </button>
            )}
          </div>
        )}
      </div>

      {creating && (
        <TicketModal
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={(t) => { setCreating(false); load(); navigate(`/tickets/${t.id}`); }}
        />
      )}
    </div>
  );
}
